import { Message } from './types'
import { useSidebarProvider } from '../../../contexts/Sidebar'
import { SystemMessage } from './components/SystemMessage'
import { UserMessage } from './components/UserMessage'
import { MarkdownRenderer } from './components/MarkdownRenderer'
import { MessageMetadata } from './components/MessageMetadata'

type MessageRendererProps = {
  message: Message
}

/**
 * MessageRenderer - Router component that renders messages based on their role
 * 
 * Message Types & Components:
 * 
 * 1. SYSTEM MESSAGES (role: 'system')
 *    └── SystemMessage
 *        - Displays confirmation messages ("✅ Edit applied successfully")
 *        - Shows error messages ("❌ Failed to apply edit")
 *        - Styled as centered, amber-colored notifications
 * 
 * 2. USER MESSAGES (role: 'user')
 *    └── UserMessage
 *        - Displays user's text input
 *        - Shows pasted images as thumbnails
 *        - Styled with orange/amber background
 * 
 * 3. ASSISTANT MESSAGES (role: 'assistant')
 *    ├── MarkdownRenderer
 *    │   ├── ReactMarkdown (with remark-gfm plugin)
 *    │   ├── Custom markdown components (headings, code, lists, tables)
 *    │   └── Error boundary for safe rendering
 *    └── MessageMetadata
 *        ├── EditProposalIndicator
 *        │   - Shows when AI has proposed document edits
 *        │   - Links to EditConfirmationDialog in parent
 *        └── ReferencedNotes
 *            - Lists notes the AI referenced
 *            - Clickable buttons to reveal notes in sidebar
 * 
 * Props Flow:
 * - message: Complete message object with role, content, metadata
 * - handleRevealNote: From SidebarProvider context, opens referenced notes
 * 
 * Error Handling:
 * - Detects error messages (content starting with "Error:")
 * - Passes isError flag to MarkdownRenderer for error styling
 */
export function MessageRenderer({ message }: MessageRendererProps) {
  const { handleRevealNote } = useSidebarProvider()

  // System messages (confirmations, errors)
  if (message.role === 'system') {
    return <SystemMessage content={message.content} />
  }

  // User messages
  if (message.role === 'user') {
    return <UserMessage content={message.content} images={message.images} />
  }

  // AI assistant messages - render markdown with metadata
  const isError = message.content.startsWith('Error:')

  return (
    <div className='flex justify-start'>
      <div className='max-w-full'>
        <div className='max-w-none'>
          {/* Render markdown content */}
          <MarkdownRenderer content={message.content} isError={isError} />
          
          {/* Show metadata (edit proposals, referenced notes) */}
          <MessageMetadata message={message} onRevealNote={handleRevealNote} />
        </div>
      </div>
    </div>
  )
}